import { useEffect, useState } from 'react';

import { revokeRoleToUser, useFetchGrantedRolesToUser } from '@jobandtalent/auth-api-client-v2';
import { FetchGrantedRolesToUser200DataUserRolesItem } from '@jobandtalent/auth-api-client-v2/dist/apiClient/model/fetchGrantedRolesToUser200DataUserRolesItem';
import { Button } from '@jobandtalent/design-system';

import Privilege from 'src/components/Privilege/Privilege';
import Section from 'src/components/Section/Section';
import useError, { DEFAULT_ERROR } from 'src/hooks/useError/useError';
import useLocalPermissions from 'src/hooks/useLocalPermissions/useLocalPermissions';
import useNotification, { DEFAULT_SUCCESS } from 'src/hooks/useNotification/useNotification';

import NewRoleForm from '../NewRoleForm/NewRoleForm';
import { useUserState } from '../UserState';

import styles from './EditUserForm.module.scss';

const UserRolesSection = () => {
  const { state } = useUserState();
  const { error, setError } = useError();
  const { notification, setNotification } = useNotification();
  const [showNewRoleForm, setShowNewRoleForm] = useState(false);
  const [selectedRole, setSelectedRole] = useState<FetchGrantedRolesToUser200DataUserRolesItem | null>();
  const { permissions, error: permissionError } = useLocalPermissions();

  const {
    data: userRolesData,
    error: userRolesError,
    mutate: userRolesMutate
  } = useFetchGrantedRolesToUser(state.user?.id as number);

  const onClickRevokeRole = async (roleId: number) => {
    if (!state.user) {
      return;
    }

    revokeRoleToUser(state.user.id, roleId).then(
      () => userRolesMutate(),
      (error) => {
        setError(error?.response?.data ? error.response.data : DEFAULT_ERROR);
      }
    );
  };

  const onCloseNewRoleForm = () => {
    setShowNewRoleForm(false);
    setSelectedRole(null);
  };

  const onSuccessNewRoleForm = () => {
    userRolesMutate();
    setNotification(DEFAULT_SUCCESS);
    setShowNewRoleForm(false);
    setSelectedRole(null);
  };

  useEffect(() => {
    if (userRolesError) {
      setError(userRolesError);
    }
  }, [userRolesError]);

  const companyAdminPermission = !permissionError && permissions.companyAdminPermission;
  const serviceUser = state.user?.service_user
  const ServiceUsersPermissions = !permissionError && permissions.serviceUsersPermission;

  return (
    <>
      <Section icon="person_accepted" title="Roles">
        {userRolesData?.data.user_roles.map((role) => (
          <Privilege
            key={role.id}
            onRevoke={(!companyAdminPermission && role.category === 'company_app') || (serviceUser && !ServiceUsersPermissions) ? undefined : () => onClickRevokeRole(role.role_id)}
            onEdit={(!companyAdminPermission && role.category === 'company_app') || (serviceUser && !ServiceUsersPermissions) ? undefined : () => {
              setShowNewRoleForm(true);
              setSelectedRole(role);
            }}
            title={role.role_name}
            subtitles={[
              role.role_scope_name || '-',
              datascopeValue(role),
              role.category === 'lite_webapp'
                ? 'Lite Webapps'
                : role.category === 'company_app'
                  ? 'Company App'
                  : ''
            ]}
          />
        ))}
        <Button
          disabled={(serviceUser && !ServiceUsersPermissions)}
          className={styles.grantPrivilege}
          dataAttributes={{
            testid: 'grantRoleButton'
          }}
          onSelect={() => setShowNewRoleForm(true)}
          secondary
          tiny
        >
          Grant role
        </Button>
      </Section>
      {showNewRoleForm && state.user && (
        <NewRoleForm
          onClose={onCloseNewRoleForm}
          onSuccess={onSuccessNewRoleForm}
          selectedUser={state.user}
          selectedRole={selectedRole}
        />
      )}
      {error}
      {notification}
    </>
  );
};

const datascopeValue = (role: FetchGrantedRolesToUser200DataUserRolesItem): string => {
  if(role.data_scope && role.data_scope.length > 0) {
    return `Data scopes: ${role.data_scope.join(", ")}`;
  }

  return "";
}

export default UserRolesSection;
